import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchKpisThunk,
  fetchChartDataThunk,
  exportChartThunk,
} from "../../../store/thunks/analyticsThunks";
import {
  setAnalyticsFilters,
  clearAnalyticsError,
} from "../../../store/slices/analyticsSlice";
import StatCard from "../../../components/charts/StatCard";
import HeatMapChart from "../../../components/charts/HeatMapChart";
import DateRangePicker from "../../../components/common/DateRangePicker";
import Select from "../../../components/common/Select";
import Button from "../../../components/common/Button";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  formatNumber,
  formatPercentage,
  getDateRangePresets,
  getGroupByOptions,
} from "../../../services/analyticsService";
import { chartColors } from "../constants/notificationConstants";
import { notifyError } from "../../../utils/notification";
import {
  PaperAirplaneIcon,
  CheckCircleIcon,
  CursorClickIcon,
  ChartBarIcon,
  DownloadIcon,
  RefreshIcon,
} from "@heroicons/react/outline";

const AnalyticsPage = () => {
  const dispatch = useDispatch();
  const { kpis, chartData, filters, loading, error } = useSelector(
    (state) => state.notificationAnalytics
  );

  console.log("AnalyticsPage - kpis:", {
    kpis,
    chartData,
    filters,
    loading,
    error,
  });

  const [activeChartTab, setActiveChartTab] = useState("timeSeries");
  const dateRangePresets = getDateRangePresets();
  const groupByOptions = getGroupByOptions();

  useEffect(() => {
    if (error) {
      notifyError(error);
      dispatch(clearAnalyticsError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    // Fetch KPIs
    dispatch(fetchKpisThunk(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    // Fetch chart data based on active tab
    const chartTypeMap = {
      timeSeries: { groupBy: "hour", range: filters.dateRange?.range || "7d" },
      funnel: { groupBy: "template", range: filters.dateRange?.range || "30d" },
      heatMap: { groupBy: "hour", range: "30d" },
      topTemplates: {
        groupBy: "template",
        range: filters.dateRange?.range || "30d",
      },
    };

    const params = chartTypeMap[activeChartTab];
    if (params) {
      dispatch(
        fetchChartDataThunk({
          chartType: activeChartTab,
          params,
        })
      );
    }
  }, [dispatch, activeChartTab, filters]);

  const handleDateRangeChange = (range) => {
    dispatch(setAnalyticsFilters({ dateRange: range }));
  };

  const handleRefresh = () => {
    dispatch(fetchKpisThunk(filters));
    dispatch(
      fetchChartDataThunk({
        chartType: activeChartTab,
        params: {
          groupBy: filters.groupBy || "hour",
          range: filters.dateRange?.range || "7d",
        },
      })
    );
  };

  const handleExportChart = async () => {
    const chartElement = document.getElementById(`chart-${activeChartTab}`);
    if (chartElement) {
      try {
        await dispatch(
          exportChartThunk({
            chartElement,
            fileName: `notification-${activeChartTab}-${
              new Date().toISOString().split("T")[0]
            }.png`,
          })
        ).unwrap();
      } catch (error) {
        notifyError("Failed to export chart");
      }
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="font-semibold">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (activeChartTab) {
      case "timeSeries":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={
                Array.isArray(chartData.timeSeries)
                  ? chartData.timeSeries
                  : []
              }
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="sent"
                stroke={chartColors.primary}
                name="Sent"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="delivered"
                stroke={chartColors.success}
                name="Delivered"
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="opened"
                stroke={chartColors.warning}
                name="Opened"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case "funnel":
        const funnelData = Array.isArray(chartData.funnel)
          ? chartData.funnel
          : [
              { name: "Sent", value: 1000, color: chartColors.primary },
              { name: "Delivered", value: 850, color: chartColors.info },
              { name: "Opened", value: 450, color: chartColors.success },
              { name: "Clicked", value: 120, color: chartColors.warning },
            ];

        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={
                Array.isArray(funnelData)
                  ? funnelData
                  : []
              }
              layout="horizontal"
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" fill={chartColors.primary}>
                {funnelData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case "heatMap":
        return (
          <HeatMapChart 
            data={
              Array.isArray(chartData.heatMap)
                ? chartData.heatMap
                : []
            } 
            loading={loading} 
          />
        );

      case "topTemplates":
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={
                Array.isArray(chartData.topTemplates)
                  ? chartData.topTemplates.slice(0, 10)
                  : []
              }
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar
                dataKey="sent"
                fill={chartColors.primary}
                name="Sent"
                radius={[8, 8, 0, 0]}
              />
              <Bar
                dataKey="opened"
                fill={chartColors.success}
                name="Opened"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Notification Analytics
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track performance and engagement metrics
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={handleRefresh}
            className="flex items-center gap-2"
          >
            <RefreshIcon className="w-4 h-4" />
            Refresh
          </Button>
          <DateRangePicker
            value={filters.dateRange}
            onChange={handleDateRangeChange}
            presets={dateRangePresets}
          />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Sent"
          value={kpis.totalSent}
          icon={PaperAirplaneIcon}
          color="primary"
          loading={loading}
        />
        <StatCard
          title="Delivery Rate"
          value={kpis.deliveryRate}
          format="percentage"
          icon={CheckCircleIcon}
          color="success"
          loading={loading}
        />
        <StatCard
          title="Open Rate"
          value={kpis.openRate}
          format="percentage"
          icon={ChartBarIcon}
          color="info"
          loading={loading}
        />
        <StatCard
          title="Click Rate"
          value={kpis.clickRate}
          format="percentage"
          icon={CursorClickIcon}
          color="warning"
          loading={loading}
        />
      </div>

      {/* Charts Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        {/* Chart Tabs */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2">
            {[
              { key: "timeSeries", label: "Time Series" },
              { key: "funnel", label: "Engagement Funnel" },
              { key: "heatMap", label: "Best Send Times" },
              { key: "topTemplates", label: "Top Templates" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveChartTab(tab.key)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeChartTab === tab.key
                    ? "bg-primary text-white"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExportChart}
            className="flex items-center gap-1"
          >
            <DownloadIcon className="w-4 h-4" />
            Export
          </Button>
        </div>

        {/* Chart Container */}
        <div id={`chart-${activeChartTab}`}>
          {loading ? (
            <div className="h-96 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
          ) : (
            renderChart()
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
